"use client";

import { useState, useCallback } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import SectionContainer from "@/common/SectionContainer";
import Pagination from "@/common/Pagination";
import ImageWithSkeleton from "@/common/ImageWithSkeleton";
import NewsroomLoading from "./components/NewsroomLoading";
import { useGetPostsList } from "@/api/useGetPostsApi";
import { useGetSiteId } from "@/api/useGetSiteApi";
import { DEFAULT_PAGE, DEFAULT_COUNT } from "@/constants/posts";
import { stripHtml } from "@/utils/stripHtml";

const NewsroomClient = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const { siteId } = useGetSiteId();
  const {
    data: postsData,
    isLoading,
    isFetching,
  } = useGetPostsList({
    page,
    count: DEFAULT_COUNT,
    search: search || undefined,
    siteId,
  });

  const list = postsData?.data.list.filter((item) => !item.isHidden) ?? [];
  const total = postsData?.data.total ?? 0;
  const totalPages = Math.ceil(total / DEFAULT_COUNT);

  const handleSearch = useCallback(() => {
    setSearch(searchInput);
    setPage(1);
  }, [searchInput]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") handleSearch();
    },
    [handleSearch],
  );

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleCardClick = useCallback(
    (id: number) => {
      router.push(`/newsroom/${id}`);
    },
    [router],
  );

  if (isLoading) return <NewsroomLoading />;

  return (
    <>
      {/* Hero Banner */}
      <div
        className="relative w-full min-h-[360px] md:min-h-[450px] overflow-hidden"
      >
        <Image
          src="/images/newsroom-bg.webp"
          alt={t("HEADER_NEWSROOM")}
          fill
          sizes="100vw"
          quality={85}
          priority
          className="object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute top-[calc(50%+40px)] left-0 -translate-y-1/2 w-full text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white" suppressHydrationWarning>
            {t("HEADER_NEWSROOM")}
          </h1>
        </div>
      </div>

      <SectionContainer className="bg-[#f5f5f5]">
        <div className="text-[#222222] px-2 sm:px-4 py-5 md:py-16 max-w-[1280px] mx-auto">
          {/* Card Grid */}
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-8 mt-5 transition-opacity duration-300 ${
              isFetching ? "opacity-40 pointer-events-none" : "opacity-100"
            }`}
          >
            {list.length > 0
              ? list.map((item) => (
                  <div
                    key={item.id}
                    className="cursor-pointer group"
                    onClick={() => handleCardClick(item.id)}
                  >
                    <div className="relative aspect-[16/9] overflow-hidden rounded-sm bg-gray-200">
                      {item.thumbnailUrl ? (
                        <ImageWithSkeleton
                          src={item.thumbnailUrl}
                          alt={item.title}
                          fill
                          sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, 33vw"
                          className="object-cover group-hover:scale-105 transition-transform duration-300"
                          unoptimized
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-200" />
                      )}
                    </div>
                    <div className="mt-4 space-y-1">
                      <h3 className="text-base md:text-lg font-semibold truncate">
                        {item.title}
                      </h3>
                      {item.content && (
                        <p className="text-xs text-gray-500 line-clamp-3">
                          {stripHtml(item.content)}
                        </p>
                      )}
                    </div>
                  </div>
                ))
              : !isFetching && (
                  <div className="col-span-full py-20 text-center text-gray-400 text-base">
                    {search ? t("NEWSROOM_SEARCH_EMPTY") : t("NEWSROOM_EMPTY")}
                  </div>
                )}
          </div>

          {/* Pagination + Search */}
          <div
            className="mt-12 flex flex-col items-center gap-4 md:grid md:items-center"
            style={{ gridTemplateColumns: "1fr 1fr 1fr" }}
          >
            {/* Desktop Search (left) */}
            <div className="hidden md:flex items-center">
              <div className="relative w-full">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                  />
                </svg>
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t("NEWSROOM_SEARCH_PLACEHOLDER")}
                  className="w-full pl-10 pr-4 py-2 border border-r-0 border-[#d1d5db] rounded-l-lg text-sm bg-white focus:outline-none focus:border-[#2b7fff] focus:ring-1 focus:ring-[#2b7fff]"
                />
              </div>
              <button
                onClick={handleSearch}
                className="px-4 py-2 border border-slate-500 bg-slate-500 text-white text-sm font-medium rounded-r-lg hover:bg-slate-600 transition-colors shrink-0"
              >
                {t("NEWSROOM_SEARCH_BUTTON")}
              </button>
            </div>

            {/* Pagination (center) */}
            <div className="flex justify-center">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
            <div />
          </div>

          {/* Mobile Search */}
          <div className="mt-4 flex items-center md:hidden">
            <div className="relative w-full">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                />
              </svg>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t("NEWSROOM_SEARCH_PLACEHOLDER")}
                className="w-full pl-10 pr-4 py-2 border border-r-0 border-[#d1d5db] rounded-l-lg text-sm bg-white focus:outline-none focus:border-[#2b7fff] focus:ring-1 focus:ring-[#2b7fff]"
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-4 py-2 border border-slate-500 bg-slate-500 text-white text-sm font-medium rounded-r-lg hover: bg-slate-600 transition-colors shrink-0"
            >
              {t("NEWSROOM_SEARCH_BUTTON")}
            </button>
          </div>
        </div>
      </SectionContainer>
    </>
  );
};

export default NewsroomClient;
